import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { generateClassLink, clearLmsLink } from "../../../redux/features/lmsSlice";

function LinkRow({ label, value }) {
    const { enqueueSnackbar } = useSnackbar();
    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ minWidth: 90 }}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </Box>
            <Typography variant="body2" sx={{ wordBreak: "break-all", flexGrow: 1 }}>{value}</Typography>
            <CopyToClipboard text={value} onCopy={() => enqueueSnackbar("Copied", { variant: "success" })}>
                <IconButton size="small">
                    <Icon icon="bx:copy-alt" width="20" height="20" color="#2f72b3" />
                </IconButton>
            </CopyToClipboard>
        </Stack>
    );
}

/** Employee-portal → Interval LMS integration, step 1 — standalone page under My Workspace so it's
 *  reachable by every logged-in employee (no navChild/permission scoping), same pattern as
 *  admission-register etc. in MyWorkspaceRouter.js. Generates a class where YOU are the host. */
export default function GenerateClassLinkPage() {
    const dispatch = useDispatch();
    const { loading, lastLink, error } = useSelector((state) => state.lms);
    const [title, setTitle] = React.useState("");

    const handleGenerate = () => {
        dispatch(clearLmsLink());
        dispatch(generateClassLink({ title: title.trim() || undefined }));
    };

    return (
        <Box sx={{ maxWidth: 560, mx: "auto", mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Generate a class link</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Creates a live class in Interval LMS with you as the host — no separate login needed.
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    label="Class title (optional)"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <Button variant="contained" onClick={handleGenerate} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : "Generate"}
                </Button>

                {error && (
                    <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
                )}
                {lastLink && (
                    <Box sx={{ mt: 3 }}>
                        <LinkRow label="You (host)" value={lastLink.hostLink} />
                        <LinkRow label="Student" value={lastLink.studentLink} />
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
